import React, { useState, useEffect, useRef } from 'react';
import PFP from '../icons/image.jpg'
import Projects from './Projects';
import Contact from './Contact';

// -------------------- Hangman Component --------------------
function Hangman({ darkMode }) {
  const WORDS = [
    'algorithm','recursion','closure','asynchronous','component',
    'promise','iterator','hoisting','immutable','polymorphism',
    'currying','webpack','typescript','debounce','refactor'
  ];

  const MAX_WRONG = 6;
  const [word, setWord] = useState('');
  const [guesses, setGuesses] = useState([]);
  const [wrongCount, setWrongCount] = useState(0);
  const [status, setStatus] = useState('playing'); // playing | won | lost
  const [score, setScore] = useState(() => Number(localStorage.getItem('hangman_score') || 0));
  const [streak, setStreak] = useState(() => Number(localStorage.getItem('hangman_streak') || 0));
  const [particles, setParticles] = useState([]);
  const confettiCanvas = useRef(null);

  useEffect(() => startNewRound(), []);

  useEffect(() => {
    localStorage.setItem('hangman_score', String(score));
  }, [score]);

  useEffect(() => {
    localStorage.setItem('hangman_streak', String(streak));
  }, [streak]);

  useEffect(() => {
    if (status === 'won') {
      runConfetti();
      setScore(s => s + 100 + Math.max(0, (MAX_WRONG - wrongCount) * 10));
      setStreak(s => s + 1);
    }
    if (status === 'lost') {
      setStreak(0);
    }
  }, [status]);

  // pick and set word
  function startNewRound() {
    const pick = WORDS[Math.floor(Math.random() * WORDS.length)].toUpperCase();
    setWord(pick);
    setGuesses([]);
    setWrongCount(0);
    setStatus('playing');
    clearConfetti();
  }

  function guessLetter(l) {
    if (status !== 'playing') return;
    if (guesses.includes(l)) return;
    const next = [...guesses, l];
    setGuesses(next);
    if (!word.includes(l)) {
      setWrongCount(w => {
        const nw = w + 1;
        if (nw >= MAX_WRONG) setStatus('lost');
        return nw;
      });
    } else {
      // check win
      const letters = [...new Set(word.split(''))];
      const corrects = letters.filter(c => next.includes(c));
      if (corrects.length === letters.length) setStatus('won');
    }
  }

  // keyboard layout
  const KEYS = 'QWERTYUIOPASDFGHJKLZXCVBNM'.split('');

  // drawing helpers for animated SVG hangman
  const strokes = [
    // base, pole, beam, rope, head, body, leftArm, rightArm, leftLeg, rightLeg
    { d: 'M10 90h80', delay: 0 },
    { d: 'M30 90V10', delay: 100 },
    { d: 'M30 10h40', delay: 200 },
    { d: 'M70 10v14', delay: 300 },
    { d: 'M70 30a8 8 0 1 0 0.001 0', delay: 400 }, // head
    { d: 'M70 38v24', delay: 500 }, // body
    { d: 'M70 46l-12 12', delay: 600 }, // left arm
    { d: 'M70 46l12 12', delay: 700 }, // right arm
    { d: 'M70 62l-12 20', delay: 800 }, // left leg
    { d: 'M70 62l12 20', delay: 900 } // right leg
  ];

  // confetti routines (basic canvas particle system)
  function runConfetti() {
    const cvs = confettiCanvas.current;
    if (!cvs) return;
    const ctx = cvs.getContext('2d');
    const w = cvs.width = cvs.offsetWidth;
    const h = cvs.height = cvs.offsetHeight;
    const parts = [];
    for (let i=0;i<80;i++) parts.push({
      x: Math.random()*w,
      y: -Math.random()*h,
      vx: (Math.random()-0.5)*6,
      vy: Math.random()*6+2,
      size: Math.random()*8+4,
      color: `hsl(${Math.random()*360},90%,60%)`
    });
    setParticles(parts);
    let raf;
    function frame(){
      ctx.clearRect(0,0,w,h);
      for (let p of parts){
        ctx.fillStyle = p.color;
        p.x += p.vx; p.y += p.vy; p.vy += 0.1;
        ctx.fillRect(p.x, p.y, p.size, p.size*0.6);
      }
      raf = requestAnimationFrame(frame);
    }
    frame();
    setTimeout(()=>{ cancelAnimationFrame(raf); ctx.clearRect(0,0,w,h); setParticles([]); }, 3500);
  }

  function clearConfetti(){
    const cvs = confettiCanvas.current;
    if (!cvs) return;
    const ctx = cvs.getContext('2d');
    ctx.clearRect(0,0,cvs.width, cvs.height);
    setParticles([]);
  }

  // render masked word
  const display = word.split('').map((c,i) => (
    <span key={i} className={`inline-block w-8 md:w-10 h-12 md:h-14 mx-1 md:mx-2 text-2xl md:text-3xl font-bold tracking-wider flex items-end justify-center transition-all duration-300 ${guesses.includes(c) ? 'text-white' : 'text-gray-500'}`}>
      {guesses.includes(c) ? c : ' '}
      <div className={`h-1 w-full mt-2 rounded ${guesses.includes(c) ? 'bg-emerald-400' : 'bg-white/20'}`}></div>
    </span>
  ));

  return (
    <section id="hangman" className={`py-20 relative transition-all duration-700 ${darkMode ? 'bg-gradient-to-b from-slate-900 via-purple-900 to-slate-800' : 'bg-gradient-to-b from-slate-800 via-purple-800 to-slate-700'}`}>
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-8 items-start">

          {/* Left - Game UI */}
          <div className="relative z-20 p-8 rounded-2xl backdrop-blur-md bg-white/6 border border-white/6 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">Hangman — Code Edition</h3>
                <p className="text-sm text-gray-300">Guess the coding-related word. Be careful — each mistake draws a little more of the hangman.</p>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-300">Score</div>
                <div className="text-xl font-bold">{score}</div>
                <div className="text-xs text-gray-300">Streak</div>
                <div className="text-xl font-bold text-amber-300">{streak}</div>
              </div>
            </div>

            {/* Animated background blobs */}
            <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full mix-blend-multiply opacity-20 filter blur-3xl bg-gradient-to-br from-cyan-400 to-purple-500 animate-pulse"></div>

            {/* Hangman SVG */}
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-full md:w-1/2 flex justify-center">
                <svg viewBox="0 0 100 110" className="w-56 h-56">
                  {/* strokes - reveal according to wrongCount with animated stroke-dashoffset */}
                  {strokes.map((s, i) => (
                    <path key={i} d={s.d} strokeWidth={2.5} stroke="#ffffff" fill="none"
                      strokeDasharray={100}
                      strokeDashoffset={i < wrongCount ? 0 : 100}
                      style={{ transition: 'stroke-dashoffset 600ms cubic-bezier(.23,1,.32,1)', strokeLinecap: 'round', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.4))' }}
                    />
                  ))}
                </svg>
              </div>

              <div className="w-full md:w-1/2">
                <div className="mb-4 flex flex-wrap justify-center">{display}</div>

                {/* Dynamic hints and progress */}
                <div className="mb-4 text-center">
                  <div className="text-sm text-gray-300">Wrong guesses: <span className="font-semibold text-red-400">{wrongCount}</span> / {MAX_WRONG}</div>
                  <div className="mt-2 h-3 w-full bg-white/10 rounded-full overflow-hidden">
                    <div className="h-3 rounded-full transition-all duration-500" style={{ width: `${(wrongCount / MAX_WRONG) * 100}%`, background: 'linear-gradient(90deg,#ff416c,#ffaf7b)' }}></div>
                  </div>
                </div>

                {/* Keyboard */}
                <div className="grid grid-cols-10 gap-2 justify-center">
                  {KEYS.map(k => {
                    const used = guesses.includes(k);
                    const correct = word.includes(k);
                    const stateClass = used ? (correct ? 'from-emerald-400 to-cyan-400 ring-2 ring-emerald-300/20' : 'from-red-500 to-pink-500 ring-2 ring-red-300/20') : 'from-gray-700 to-gray-600';
                    return (
                      <button
                        key={k}
                        onClick={() => guessLetter(k)}
                        disabled={used || status !== 'playing'}
                        className={`py-2 rounded-md text-sm font-bold text-white shadow-md transform hover:scale-105 transition-transform duration-150 bg-gradient-to-br ${stateClass} ${used ? 'opacity-90 cursor-not-allowed' : ''} focus:outline-none`}
                      >
                        {k}
                      </button>
                    );
                  })}
                </div>

                {/* Controls */}
                <div className="mt-6 flex gap-3 justify-center">
                  <button onClick={startNewRound} className="px-6 py-3 rounded-full font-bold bg-gradient-to-r from-cyan-500 to-purple-600 shadow-lg hover:scale-105 transition-transform">New Word</button>
                  <button onClick={() => { setScore(0); setStreak(0); }} className="px-4 py-3 rounded-full border border-white/10 text-sm">Reset Score</button>
                </div>
              </div>
            </div>

            {/* Win/Lose Overlay */}
            {status !== 'playing' && (
              <div className="absolute inset-0 rounded-2xl flex items-center justify-center bg-black/40 backdrop-blur-md">
                <div className="text-center p-8 rounded-xl bg-gradient-to-br from-white/6 to-white/3 border border-white/10 shadow-2xl max-w-sm">
                  <h2 className="text-4xl font-black mb-2 text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 to-cyan-400">
                    {status === 'won' ? 'You Slayed It 🔥' : 'Game Over'}
                  </h2>
                  <p className="mb-4 text-gray-300">The word was: <span className="font-bold text-white">{word}</span></p>
                  <div className="flex gap-3 justify-center">
                    <button onClick={startNewRound} className="px-6 py-3 rounded-full font-bold bg-gradient-to-r from-cyan-500 to-purple-600 shadow-lg">Play Again</button>
                    <button onClick={() => { setStatus('playing'); setGuesses([]); setWrongCount(0);} } className="px-4 py-3 rounded-full border border-white/10">Try Same</button>
                  </div>
                </div>
              </div>
            )}

            <canvas ref={confettiCanvas} className="absolute inset-0 w-full h-full pointer-events-none"></canvas>
          </div>

          {/* Right - Info / Tips */}
          <div className="p-8 rounded-2xl backdrop-blur-md bg-white/6 border border-white/6 shadow-2xl">
            <h4 className="text-2xl font-bold mb-2 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">Pro Tips</h4>
            <ul className="text-gray-300 list-disc ml-5 space-y-2 text-sm">
              <li>Words are coding-themed — think function names, patterns and tools.</li>
              <li>Each wrong guess reveals more of the hangman — you have {MAX_WRONG} tries.</li>
              <li>Correct letters glow green, wrong ones glow red. Keep your streak to earn bonus points.</li>
              <li>Responsive & glassmorphic — plays nicely on mobile.</li>
            </ul>

            <div className="mt-6">
              <h5 className="text-sm text-gray-300 mb-2">Progress</h5>
              <div className="w-full bg-white/6 h-4 rounded-full overflow-hidden">
                <div className="h-4 rounded-full transition-all duration-500" style={{ width: `${(score % 1000) / 10}%`, background: 'linear-gradient(90deg,#7afcff,#b76bff)' }}></div>
              </div>
            </div>

            <div className="mt-6 text-sm text-gray-300">Design matches your portfolio: gradients, glass, smooth animations and a sexy, modern vibe. Toggle dark mode from the main navbar — the hangman adapts.</div>
          </div>
        </div>
      </div>
    </section>
  );
}

// -------------------- TopSection Component (modified nav) --------------------
function TopSection({ darkMode, setDarkMode }) {
  const [isVisible, setIsVisible] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [showCursor, setShowCursor] = useState(true);
  
  const fullName = "Fareed";
  const rotatingWords = ["Web Developer", "Problem Solver", "Code Artist", "Digital Creator"];

  const toggleDarkMode = () => setDarkMode(!darkMode);

  // Mock SVG Icons
  const SunIcon = ({ className }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 7a5 5 0 100 10 5 5 0 000-10zM12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );

  const MoonIcon = ({ className }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  useEffect(() => {
    setIsVisible(true);
    
    // Typewriter effect for name
    let nameIndex = 0;
    const nameTimer = setInterval(() => {
      if (nameIndex <= fullName.length) {
        setTypedName(fullName.slice(0, nameIndex));
        nameIndex++;
      } else {
        clearInterval(nameTimer);
      }
    }, 150);

    // Rotating words effect
    const wordTimer = setInterval(() => {
      setCurrentWordIndex((prev) => (prev + 1) % rotatingWords.length);
    }, 2500);

    // Cursor blink
    const cursorTimer = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);
    
    return () => {
      clearInterval(nameTimer);
      clearInterval(wordTimer);
      clearInterval(cursorTimer);
    };
  }, []);

  return (
    <div className={`${darkMode 
      ? 'bg-gradient-to-b from-slate-900 via-purple-900 to-slate-800' 
      : 'bg-gradient-to-b from-slate-800 via-purple-800 to-slate-700'
    } min-h-screen transition-all duration-700 ease-in-out relative overflow-hidden`}>
      
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-10 w-96 h-96 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-10 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-2000"></div>
        <div className="absolute bottom-40 left-20 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-4000"></div>
      </div>

      {/* Floating code snippets */}
      <div className="absolute inset-0 pointer-events-none">
        {['</>', '{}', '[]', '()', '&&', '=>', '++', '??'].map((symbol, i) => (
          <div
            key={i}
            className="absolute text-white/10 font-mono text-2xl animate-float"
            style={{
              left: `${10 + (i * 12)}%`,
              top: `${15 + (i * 8)}%`,
              animationDelay: `${i * 0.5}s`,
              animationDuration: `${4 + Math.random() * 2}s`
            }}
          >
            {symbol}
          </div>
        ))}
      </div>

      {/* Enhanced Header */}
      <header className={`relative z-20 flex justify-between p-6 flex-col sm:flex-row backdrop-blur-sm transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
        
        {/* Logo Section */}
        <div className="group text-center sm:text-left">
          <div className="relative inline-block">
            <h1 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 tracking-wider hover:scale-105 transition-transform duration-300 cursor-pointer">
              Fareed
            </h1>
            <div className="absolute -bottom-1 left-0 h-0.5 w-0 bg-gradient-to-r from-cyan-400 to-purple-600 group-hover:w-full transition-all duration-500"></div>
          </div>
          <p className="text-sm text-gray-300 font-medium mt-1 opacity-80">
            {rotatingWords[currentWordIndex]}
            <span className={`ml-1 ${showCursor ? 'opacity-100' : 'opacity-0'} transition-opacity`}>|</span>
          </p>
        </div>

        {/* Enhanced Navigation - added Hangman */}
        <nav className="flex space-x-8 text-white mt-6 sm:mt-0 justify-center sm:justify-start">
          {['Home', 'About', 'Projects', 'Skills', 'Contact', 'Hangman'].map((item, index) => (
            <a 
              key={item}
              href={`#${item.toLowerCase()}`}
              onClick={(e) => {
                e.preventDefault();
                const element = document.getElementById(item.toLowerCase());
                if (element) {
                  element.scrollIntoView({ 
                    behavior: 'smooth',
                    block: 'start'
                  });
                }
              }}
              className="relative group font-medium text-gray-300 hover:text-white transition-colors duration-300 cursor-pointer"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <span className="relative z-10">{item}</span>
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-full scale-0 group-hover:scale-100 transition-transform duration-300 -m-2"></div>
              <div className="absolute -bottom-1 left-0 h-0.5 w-0 bg-gradient-to-r from-cyan-400 to-purple-600 group-hover:w-full transition-all duration-300"></div>
            </a>
          ))}
        </nav>
        
        {/* Enhanced Dark Mode Toggle */}
        <div className="flex items-center mt-6 sm:mt-0 justify-center sm:justify-end">
          <div 
            onClick={toggleDarkMode} 
            className="relative cursor-pointer flex items-center w-16 h-8 rounded-full bg-gradient-to-r from-gray-700 to-gray-600 p-1 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          >
            {/* Enhanced Toggle Background */}
            <div className={`absolute inset-1 rounded-full bg-gradient-to-r ${darkMode ? 'from-purple-600 to-blue-600' : 'from-yellow-400 to-orange-500'} transition-all duration-500`}></div>
            
            {/* Toggle Ball */}
            <div 
              className={`relative z-10 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 ease-out transform ${darkMode ? 'translate-x-8' : 'translate-x-0'} flex items-center justify-center`}
            >
              {darkMode ? (
                <MoonIcon className="w-4 h-4 text-purple-600" />
              ) : (
                <SunIcon className="w-4 h-4 text-orange-500" />
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Epic Hero Section */}
      <section className="relative z-10 flex justify-center items-center min-h-screen text-center text-white px-6 py-20">
        <div className={`max-w-4xl transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          
          {/* Greeting */}
          <div className="mb-8">
            <span className="inline-block px-6 py-2 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 backdrop-blur-sm rounded-full border border-cyan-500/30 text-cyan-400 font-semibold text-lg mb-6 animate-pulse">
              👋 Welcome to my digital space
            </span>
          </div>

          {/* Main Heading with Typewriter */}
          <div className="mb-6">
            <h1 className="text-6xl md:text-8xl font-black mb-4 leading-tight">
              <span className="text-gray-400">Hi, I'm </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400">
                {typedName}
                <span className={`${showCursor && typedName.length < fullName.length ? 'opacity-100' : 'opacity-0'} transition-opacity`}>|</span>
              </span>
            </h1>
          </div>

          {/* Dynamic Subtitle */}
          <div className="mb-8 h-16 flex items-center justify-center">
            <p className="text-2xl md:text-3xl text-gray-300 font-light">
              A <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">
                {rotatingWords[currentWordIndex]}
              </span>
            </p>
          </div>

          {/* Description */}
          <p className="text-xl md:text-2xl text-gray-400 mb-10 max-w-3xl mx-auto leading-relaxed">
            Passionate about crafting <span className="text-purple-400 font-semibold">innovative digital solutions</span> and 
            transforming ideas into <span className="text-cyan-400 font-semibold">beautiful, functional experiences</span>.
          </p>

          {/* Enhanced CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <button className="group relative px-10 py-4 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full font-bold text-lg shadow-2xl hover:shadow-purple-500/25 transition-all duration-300 transform hover:scale-105 overflow-hidden">
              <span className="relative z-10 flex items-center">
                Get in Touch
                <svg className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </button>

            <button className="group px-10 py-4 border-2 border-gray-600 rounded-full font-bold text-lg text-gray-300 hover:text-white hover:border-cyan-400 transition-all duration-300 transform hover:scale-105 backdrop-blur-sm">
              <span className="flex items-center">
                View My Work
                <svg className="ml-2 w-5 h-5 group-hover:translate-y-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </span>
            </button>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center">
              <div className="w-1 h-3 bg-gradient-to-b from-cyan-400 to-purple-600 rounded-full mt-2 animate-pulse"></div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// -------------------- About Component (unchanged except useEffect fixes) --------------------
function About({ darkMode }) {
  const [isVisible, setIsVisible] = useState(false);
  const [activeSkill, setActiveSkill] = useState(null);
  const [typedText, setTypedText] = useState('');
  
  const fullText = "Hi, I'm Fareed 👋";
  const skills = [
    { name: 'Frontend Magic', tech: 'HTML, CSS, JavaScript', icon: '✨', color: 'from-purple-500 to-pink-500' },
    { name: 'React Wizardry', tech: 'React, Tailwind CSS', icon: '⚛️', color: 'from-blue-500 to-cyan-500' },
    { name: 'Backend Power', tech: 'Node.js, Express', icon: '⚡', color: 'from-green-500 to-emerald-500' },
    { name: 'Design Vision', tech: 'UI/UX, Web Design', icon: '🎨', color: 'from-orange-500 to-red-500' }
  ];

 
  useEffect(() => {
    setIsVisible(true);
    
    // Typewriter effect
    let index = 0;
    const timer = setInterval(() => {
      if (index <= fullText.length) {
        setTypedText(fullText.slice(0, index));
        index++;
      } else {
        clearInterval(timer);
      }
    }, 100);
    
    return () => clearInterval(timer);
  }, []);

  return (
    <section id="about" className={`relative py-20 overflow-hidden transition-all duration-700 ${
      darkMode 
        ? 'bg-gradient-to-b from-slate-800 via-purple-900 to-slate-900' 
        : 'bg-gradient-to-b from-slate-700 via-purple-800 to-slate-800'
    }`}>
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute top-0 right-4 w-72 h-72 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-4000"></div>
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`
            }}
          >
            <div className="w-1 h-1 bg-white rounded-full opacity-60"></div>
          </div>
        ))}
      </div>

      <div className="relative container mx-auto px-6">
        <div className={`md:flex md:items-center md:space-x-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          
          {/* Enhanced Text Content */}
          <div className="md:w-1/2 text-center md:text-left">
            <div className="mb-6">
              <h2 className="text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 mb-2 tracking-tight">
                About Me
              </h2>
              <div className="h-1 w-24 bg-gradient-to-r from-cyan-400 to-purple-600 rounded-full mx-auto md:mx-0"></div>
            </div>
            
            <div className="mb-8">
              <p className="text-2xl font-semibold text-white mb-2 min-h-[2rem]">
                {typedText}
                <span className="animate-pulse">|</span>
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                A passionate <span className="text-cyan-400 font-semibold">web developer</span> who transforms ideas into 
                <span className="text-purple-400 font-semibold"> sleek digital experiences</span>. I craft modern, 
                functional websites with a focus on user-centered design and cutting-edge technologies.
              </p>
            </div>

            {/* Interactive Skills Grid */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-white mb-4">My Arsenal</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {skills.map((skill, index) => (
                  <div
                    key={index}
                    className={`relative p-4 rounded-xl cursor-pointer transition-all duration-300 transform hover:scale-105 ${
                      activeSkill === index 
                        ? `bg-gradient-to-r ${skill.color} shadow-2xl` 
                        : 'bg-white/10 backdrop-blur-sm hover:bg-white/20'
                    }`}
                    onMouseEnter={() => setActiveSkill(index)}
                    onMouseLeave={() => setActiveSkill(null)}
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{skill.icon}</span>
                      <div>
                        <h4 className="font-bold text-white text-sm">{skill.name}</h4>
                        <p className="text-gray-300 text-xs">{skill.tech}</p>
                      </div>
                    </div>
                    {activeSkill === index && (
                      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-white/20 to-transparent"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Call to action */}
            <div className="mt-8">
              <button className="group relative px-8 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full font-semibold text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 overflow-hidden">
                <span className="relative z-10">Let's Build Something Amazing</span>
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
            </div>
          </div>

          {/* Enhanced Image Section */}
          <div className="mt-12 md:mt-0 md:w-1/2 flex justify-center">
            <div className="relative group">
              {/* Rotating border */}
              <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-full animate-spin-slow opacity-75 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              {/* Main image container */}
              <div className="relative">
                <img 
                  src={PFP}
                  alt="Fareed - Web Developer" 
                  className="relative rounded-full w-64 h-64 object-cover shadow-2xl transition-transform duration-500 ease-in-out transform group-hover:scale-110 z-10"
                />
                
                {/* Floating elements around image */}
                <div className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-full flex items-center justify-center text-white font-bold animate-bounce">
                  💻
                </div>
                <div className="absolute -bottom-4 -left-4 w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-600 rounded-full flex items-center justify-center text-white font-bold animate-bounce animation-delay-1000">
                  🚀
                </div>
                <div className="absolute top-1/2 -left-8 w-8 h-8 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center text-white text-sm animate-pulse">
                  ⚡
                </div>
                <div className="absolute top-1/4 -right-6 w-8 h-8 bg-gradient-to-br from-orange-400 to-red-600 rounded-full flex items-center justify-center text-white text-sm animate-pulse animation-delay-2000">
                  ✨
                </div>
              </div>
              
              {/* Hover overlay */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-500/20 via-purple-500/20 to-pink-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <span className="text-white font-bold text-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-150">
                  Ready to Code! 🔥
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// -------------------- Main Portfolio Component --------------------
function Portfolio() {
  const [darkMode, setDarkMode] = useState(false);

  return (
    <div>
      <section id="home">
        <TopSection darkMode={darkMode} setDarkMode={setDarkMode} />
      </section>
      <About darkMode={darkMode} />
      <section id="projects"><Projects darkMode={darkMode} /></section>
      <section id="hangman"><Hangman darkMode={darkMode} /></section>
       <section id="contact">
        <Contact darkMode={darkMode} />
      </section>
      
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(180deg); }
        }
        
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
        
        .animation-delay-1000 {
          animation-delay: 1s;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
}

export default Portfolio;
